import BackToProjects from "./components/BackToProjects";
import ProjectManagementHeader from "./components/ProjectManagementHeader";
import ProjectDetailsTable from "./components/ProjectDetailsTable";
import ProjectVideos from "./components/ProjectVideos";

import { TProductInfo, TResponseOrderDetails } from "@/types/projectType";
import {
  useGetAssignedActorV2Query,
  useGetCreatorProjectDetailsQuery,
  useGetCreatorProjectDetailsV2Query,
} from "@/redux/feature/creatorProject/creatorProjectApi";
import { useParams, useSearchParams } from "react-router-dom";
import { TOrderManagementRequiredAction } from "@/redux/feature/creatorProject/creatorProjectTypes";
import { useGetOrderVideoStageStatusQuery } from "@/redux/feature/orderManagement/orderVideoStatus/orderVideoStatusApi";
import RecordingStage from "./stages/RecordingStage/RecordingStage";
import EditingStage from "./stages/EditingStage/EditingStage";
import PlanningStage from "./stages/PlanningStage/PlanningStage";
import usePolling from "@/hooks/usePolling";
import Modal from "@/components/ui/Modal";
import { useEffect, useState } from "react";
import { title } from "process";
import { X } from "lucide-react";
import useOrderVideoParams from "@/hooks/useOrderVideoParams";
import { ORDER_PLANS_IDS } from "@/constant";
import ProductDetailsModal from "./components/ProductDetailsModal";

/**
 * Might Need to chaange logic
 * On creator dashboard
 * while applying
 * after uploading the raw and videsetup
 *
 */

type TProjectDetails = {
  video_id: string;
  order_id: string;
  background_image?: string;
  wardrobe_image?: string;
};

const ProjectManagement = () => {
  const [selectedImage, setSelectedImage] = useState({
    title: "",
    img: "",
  });
  const [showProductModal, setShowProductModal] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const { orderId, videoId } = useOrderVideoParams();
  const [showContent, setShowContent] = useState(false);

  const pollingTemplates = {
    order: [
      "client-script-revision-requested-001",
      "client-script-approved-001",
      "admin-planning-stage-completed-007",
      "admin-recording-stage-completed-008",
      "admin-order-complete-006",
    ],
  };

  // Create polling intervals
  const orderDetailsPollingInterval = usePolling({
    templateIds: pollingTemplates.order,
  }).pollingInterval;

  const { data, isLoading } = useGetCreatorProjectDetailsV2Query(orderId, {
    refetchOnMountOrArgChange: true,
    pollingInterval: orderDetailsPollingInterval,
  });
  const OrderDetails: TResponseOrderDetails = data?.data?.orderDetails;
  const ProjectDetails: TProjectDetails[] = data?.data?.projectDetails;
  const CurrentProjectDetails = ProjectDetails?.find(
    (item) => item?.video_id === videoId
  );
  const OrderCurrency = OrderDetails?.paymentDetails?.currency;

  const ActiveVideo = OrderDetails?.orderVideos?.find(
    (item) => item?.video_id === videoId
  );
  const RequiredActions = data?.data
    ?.requiredActions as TOrderManagementRequiredAction;

  // get the video stage status
  const { data: VideoStageStatusRes } = useGetOrderVideoStageStatusQuery(
    {
      orderId: orderId,
      videoId,
    },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: orderDetailsPollingInterval,
    }
  );
  const VideoStageStatus = VideoStageStatusRes?.data;
  const statusForCurrentVideo = VideoStageStatus?.find(
    (item: any) => item?.video_id === videoId
  );
  const isFeaturingProduct =
    ActiveVideo?.scriptDetails.primaryGoal.id === "promotingProductorService";
  const isOverlayProduct = ActiveVideo?.scriptDetails.isOverlayProduct;
  const { data: assignedActorData } = useGetAssignedActorV2Query(
    { orderId, videoId: videoId },
    {
      skip: !orderId || !videoId,
      refetchOnMountOrArgChange: true,
    }
  );
  const creatorAssignmentData = assignedActorData?.data;

  const handlePreviewBGorOutfit = ({
    img,
    title,
  }: {
    img: string;
    title: string;
  }) => {
    setSelectedImage({
      img,
      title,
    });
    setIsOpen(true);
  };

  const isFulfilledByCreator =
    OrderDetails?.orderPlanDetails?.selectedVideoPlan?._id ===
      ORDER_PLANS_IDS.CREATOR_CANVAS &&
    creatorAssignmentData?.deliverables === "shoot_and_edit";

  const isExpressDelivery =
    ActiveVideo?.scriptDetails?.deliverySlot?.id === "express_delivery";

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading]);
  return (
    <div className="relative">
      {/* Skeleton Loader */}
      <div
        className={`absolute top-0 left-0 w-full h-full z-40 transition-opacity duration-500 ${
          isLoading || !showContent
            ? "opacity-100"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <LoadingPlaceholder />
      </div>
      {!isLoading && (
        <>
          <BackToProjects />
          <div className="px-4 md:px-11 py-9 mx-auto mb-20 md:mb-0">
            <ProjectManagementHeader data={OrderDetails} />
            <ProjectDetailsTable data={OrderDetails} />
            <div className="flex flex-col md:flex-row md:border border-[#DFDFDF] rounded-xl mt-11 mb-5">
              {/* Sidebar */}
              <div className="pb-8 md:py-6 md:px-4 md:border-r border-[#DFDFDF]">
                <h5 className="text-sm leading-5 font-semibold ">
                  {OrderDetails?.orderPlanDetails?.selectedVideoType?.title}
                </h5>
                <div className="mt-3 md:mt-5 min-w-[240px] flex md:flex-col items-start overflow-x-auto gap-2">
                  {OrderDetails?.orderVideos?.map((item, i) => {
                    return (
                      <ProjectVideos
                        index={i + 1}
                        VideoStageStatus={VideoStageStatus}
                        RequiredActions={RequiredActions}
                        data={data}
                        item={item}
                        key={i}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Main Content */}
              {!isLoading && ActiveVideo && (
                <>
                  <div className="py-4 md:px-5 md:py-4 w-full">
                    <div className="flex justify-between items-center ">
                      <h5 className="text-sm max-w-[300px] truncate  font-medium text-black">
                        {ActiveVideo?.scriptDetails.titleOfTheVideo}
                      </h5>

                      {/* <div className="text-xs text-[#6941C6]">View Activity</div> */}
                    </div>
                    <div className="flex gap-x-4  my-[16px] items-center">
                      <p className="text-[12px] text-[#98A2B3]">
                        Background :{" "}
                        <span className="text-[#667085] font-medium ">
                          {ActiveVideo?.videoEditingDetails?.background}
                        </span>
                      </p>
                      {/* <p className="text-[12px] text-[#98A2B3]">
                        Tone :{" "}
                        <span className="text-[#667085] font-medium ">
                          {ActiveVideo?.scriptDetails?.videoTone?.title}{" "}
                        </span>{" "}
                      </p> */}
                      {/* <p className="text-[12px] text-[#98A2B3]">
                        Language :{" "}
                        <span className="text-[#667085] font-medium ">
                          {ActiveVideo?.scriptDetails?.language?.title}{" "}
                        </span>{" "}
                      </p> */}
                      {CurrentProjectDetails?.background_image && (
                        <p className="text-[12px] text-[#98A2B3]">
                          Selected Background :{" "}
                          <span
                            onClick={() =>
                              handlePreviewBGorOutfit({
                                img: CurrentProjectDetails?.background_image as string,
                                title: "Selected Background",
                              })
                            }
                            className="text-[#667085] cursor-pointer underline font-medium "
                          >
                            View
                          </span>{" "}
                        </p>
                      )}
                      {CurrentProjectDetails?.wardrobe_image && (
                        <p className="text-[12px] text-[#98A2B3]">
                          Selected Outfit :{" "}
                          <span
                            onClick={() =>
                              handlePreviewBGorOutfit({
                                img: CurrentProjectDetails?.wardrobe_image as string,
                                title: "Selected Outfit",
                              })
                            }
                            className="text-[#667085] cursor-pointer underline font-medium "
                          >
                            View
                          </span>{" "}
                        </p>
                      )}

                      <p className="text-[12px] text-[#98A2B3]">
                        Language :{" "}
                        <span className="text-[#667085] font-medium ">
                          {ActiveVideo?.scriptDetails?.language?.title}{" "}
                        </span>{" "}
                      </p>

                      {isFeaturingProduct && (
                        <div>
                          <p className="text-[12px] font-medium ">
                            <span className="text-[#98A2B3]">Product:</span>{" "}
                            {ActiveVideo?.scriptDetails?.featuredProduct
                              ?.map((item) => {
                                const name = item.name || "";
                                return name.length > 30
                                  ? name.slice(0, 30) + "..."
                                  : name;
                              })
                              .join(", ")}{" "}
                            FE{" "}
                            <span
                              onClick={() => setShowProductModal(true)}
                              className="text-[#6941C6] cursor-pointer"
                            >
                              (View Product)
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-5">
                      {isFulfilledByCreator &&
                        isFeaturingProduct &&
                        !isOverlayProduct && (
                          <PlanningStage
                            VideoStageStatus={statusForCurrentVideo}
                            requiredActions={
                              RequiredActions?.PlanningStageRequiredActions
                            }
                            data={OrderDetails}
                          />
                        )}
                      <RecordingStage
                        disabled={
                          !statusForCurrentVideo?.planing_stage?.completed
                        }
                        VideoStageStatus={statusForCurrentVideo}
                        hideVideoSetup={!isExpressDelivery}
                        requiredActions={
                          RequiredActions?.RecordingStageRequiredActions
                        }
                        data={OrderDetails}
                      />
                      {isFulfilledByCreator && (
                        <EditingStage
                          disabled={
                            statusForCurrentVideo?.planing_stage?.completed
                          }
                          VideoStageStatus={statusForCurrentVideo}
                          requiredActions={
                            RequiredActions?.EditingStageRequiredActions
                          }
                          data={OrderDetails}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Image Preview Modal */}
            {isFeaturingProduct && (
              <Modal
                width="618px"
                setOpen={setShowProductModal}
                isOpen={showProductModal}
              >
                <ProductDetailsModal
                  orderCurrency={OrderCurrency}
                  productInfo={
                    ActiveVideo?.scriptDetails.featuredProduct as TProductInfo[]
                  }
                />
              </Modal>
            )}
            <Modal width="320px" isOpen={isOpen} setOpen={setIsOpen}>
              <div className="flex justify-between items-center pb-4">
                <h2 className="text-lg font-medium">{selectedImage.title}</h2>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setSelectedImage({ img: "", title: "" });
                  }}
                >
                  <X />
                </button>
              </div>
              <div className="max-w-[300px] overflow-hidden rounded-lg">
                <img
                  src={selectedImage.img}
                  className="object-cover bg-center"
                  alt=""
                />
              </div>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectManagement;

// Utility function for loading placeholder
const LoadingPlaceholder = () => (
  <div>
    {/* Desktop Placeholder */}
    <div className="py-24 md:block hidden px-8 w-full absolute top-0 left-0 z-50 opacity-100 h-screen">
      {/* Title */}
      <div className="flex justify-between">
        <div className="bg-gray-300  delay-[120ms]  rounded-full w-[200px] h-[40px]"></div>
        <div className="bg-gray-300  delay-[120ms]  rounded-full w-[200px] h-[40px]"></div>
      </div>
      {/* Header Table */}
      <div className="flex gap-4">
        <div className="animate-puls-t delay-75 duration-1000 bg-gray-200 mt-8 rounded-lg w-[120px] h-[45px]" />
        <div className="animate-puls-t delay-[300ms] duration-[900ms] bg-gray-200 mt-8 rounded-lg w-[100px] h-[45px]" />
        <div className="animate-puls-t delay-[200ms] duration-700 bg-gray-200 mt-8 rounded-lg w-[80px] h-[45px]" />
        <div className="animate-puls-t delay-[100ms] duration-[850ms] bg-gray-200 mt-8 rounded-lg w-[120px] h-[45px]" />
        <div className="animate-puls-t delay-[180ms] duration-700 bg-gray-200 mt-8 rounded-lg w-[120px] h-[45px]" />
        <div className="animate-puls-t delay-[250ms] duration-[950ms] bg-gray-200 mt-8 rounded-lg w-[100px] h-[45px]" />
        <div className="animate-puls-t delay-[90ms] duration-700 bg-gray-200 mt-8 rounded-lg w-[80px] h-[45px]" />
        <div className="animate-puls-t delay-[400ms] duration-[1100ms] bg-gray-200 mt-8 rounded-lg w-[200px] h-[45px]" />
      </div>
      <div className="px-3">
        <div className="flex flex-col gap-3 md:flex-row rounded-xl mt-11 mb-5">
          {/* Sidebar */}
          <div className="pb-8 w-[330px]  rounded-xl md:py-6 md:px-4 md:border-r  animate-puls-t delay-75 bg-gray-200 h-[700px] border-[#DFDFDF]">
            <div className="w-full  animate-puls-t delay-75 bg-gray-100 h-[25px] rounded-full"></div>
            <div className="w-full  animate-puls-t delay-100 bg-gray-300 h-[46px] mt-5 z-50 relative rounded-lg"></div>
            <div className="w-full  animate-puls-t delay-200 bg-gray-300 h-[46px] mt-5 z-50 relative rounded-lg"></div>
            <div className="w-full  animate-puls-t delay-300 bg-gray-300 h-[46px] mt-5 z-50 relative rounded-lg"></div>
            <div className="w-full  animate-puls-t delay-500 bg-gray-300 h-[46px] mt-5 z-50 relative rounded-lg"></div>
          </div>

          {/* Main Content */}
          <div className="md:px-5   animate-puls-t delay-delay-[250ms] bg-gray-200 rounded-xl h-[700px] md:py-4 w-full">
            <div>
              <div className=" mb-5  md:block hidden">
                <div className="flex justify-between items-center">
                  <div className="w-[300px]  animate-puls-t delay-75 bg-gray-300 h-[20px]  z-50 relative rounded-lg"></div>
                  <div className="w-[300px]  animate-puls-t  delay-[400ms]  bg-gray-300 h-[20px]  z-50 relative rounded-lg"></div>
                </div>

                <div className="w-full  animate-puls-t delay-75 bg-gray-300 h-[150px] mt-5 z-50 relative rounded-lg"></div>
                <div className="w-full  animate-puls-t delay-200 bg-gray-300 h-[150px] mt-5 z-50 relative rounded-lg"></div>
                <div className="w-full  animate-puls-t delay-75 bg-gray-300 h-[150px] mt-5 z-50 relative rounded-lg"></div>
              </div>
            </div>

            <div className="space-y-4 md:space-y-5">{/* </div> */}</div>
          </div>
        </div>
      </div>
    </div>
    {/* Mobile Placeholder */}
    <div className="flex justify-center items-center h-screen ">
      <p className="animate-pulse">Loading Order Data</p>
    </div>
  </div>
);
